import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, type Me } from './lib/api';
import Landing from './screens/Landing';
import Login from './screens/Login';
import Dashboard from './screens/Dashboard';
import Scenario from './screens/Scenario';
import Debrief from './screens/Debrief';
import Replay from './screens/Replay';
import Try from './screens/Try';
import Tour from './screens/Onboarding/Tour';
import MCQ from './screens/MCQ';
import RhythmFlashcards from './screens/RhythmFlashcards';
import InstructorHome from './screens/Instructor/Home';

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined); // undefined = loading

  useEffect(() => {
    api.me().then((u) => setMe(u)).catch(() => setMe(null));
  }, []);

  if (me === undefined) return <div className="h-full grid place-items-center text-mono-white">Loading…</div>;

  return (
    <div className="min-h-full flex flex-col">
      {/* Skip-to-content link for keyboard / screen-reader users.
          Hidden until focused, then becomes a prominent button at the top-left. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-brand-600 focus:text-white focus:px-3 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>
      <header className="border-b border-ink-800 bg-ink-900/80 backdrop-blur supports-[backdrop-filter]:bg-ink-900/60">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 h-14">
          <Link to="/" className="font-semibold text-mono-white tracking-tight">ALS&nbsp;Sim</Link>
          <nav className="flex items-center gap-4 text-sm">
            {me ? (
              <>
                <Link to="/dashboard" className="text-mono-white/80 hover:text-mono-white">Dashboard</Link>
                {(me.role === 'instructor' || me.role === 'admin' || me.role === 'superadmin') && (
                  <Link to="/instructor" className="text-mono-white/80 hover:text-mono-white">Instructor</Link>
                )}
                <span className="text-mono-white/60">{me.display_name}</span>
                <button onClick={async () => { await api.logout(); window.location.href = '/'; }} className="btn-ghost text-sm py-1 px-3">Log out</button>
              </>
            ) : (
              <Link to="/login" className="btn-primary text-sm py-1 px-3">Sign in</Link>
            )}
          </nav>
        </div>
      </header>

      <main id="main-content" className="flex-1">
        <Routes>
          <Route path="/" element={<Landing me={me} />} />
          <Route path="/try" element={<Try />} />
          <Route path="/login" element={me ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/onboarding" element={me ? <Tour /> : <Navigate to="/login" />} />
          <Route path="/mcq" element={me ? <MCQ /> : <Navigate to="/login" />} />
          <Route path="/flashcards" element={me ? <RhythmFlashcards /> : <Navigate to="/login" />} />
          <Route path="/dashboard" element={me ? <Dashboard me={me} /> : <Navigate to="/login" />} />
          <Route path="/scenario/:id" element={me ? <Scenario me={me} /> : <Navigate to="/login" />} />
          <Route path="/debrief/:run_id" element={me ? <Debrief /> : <Navigate to="/login" />} />
          <Route path="/debrief/:run_id/replay" element={me ? <Replay /> : <Navigate to="/login" />} />
          <Route path="/instructor/*" element={me && me.role !== 'learner' ? <InstructorHome me={me} /> : <Navigate to="/" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <footer className="border-t border-ink-800 text-xs text-mono-white/40 py-3">
        <div className="max-w-7xl mx-auto px-4 flex justify-between">
          <span>ALS Sim · Practice tool · Not for clinical decision-making.</span>
          <span>v0.1.0</span>
        </div>
      </footer>
    </div>
  );
}

function NotFound() {
  return (
    <div className="max-w-3xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-semibold">Not found</h1>
      <p className="text-mono-white/60 mt-2">That page doesn't exist.</p>
      <Link to="/" className="btn-primary mt-6 inline-block">Back to start</Link>
    </div>
  );
}
